import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED, fmtDate } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Plus, Trash2, Wallet, Pencil, Banknote, Paperclip, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const CATEGORIES = ['Groceries', 'Vegetables', 'Meat & Poultry', 'Dairy', 'Beverages', 'Gas / Fuel', 'Rent', 'Salaries', 'Utilities', 'Maintenance', 'Marketing', 'Packaging', 'Other'];
const MODES = ['cash', 'card', 'bank', 'upi'];
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = { date: today(), category: CATEGORIES[0], description: '', payee: '', amount: '', due_date: '', note: '', receipt_url: '', pay_state: 'paid', paid_now: '', payment_mode: 'cash' };
const emptyPay = { amount: '', date: today(), method: 'cash', reference: '', notes: '' };

const statusBadge = (s) => s === 'paid'
  ? <Badge className="bg-success text-white">Paid</Badge>
  : s === 'partial' ? <Badge className="bg-amber-500 text-white">Partially paid</Badge>
  : <Badge variant="destructive">Unpaid</Badge>;

export default function Expenses() {
  const { currentOutlet } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [filters, setFilters] = useState({ start: '', end: '', category: 'all', status: 'all', q: '' });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [payFor, setPayFor] = useState(null); // expense object in payments dialog
  const [pay, setPay] = useState(emptyPay);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (currentOutlet) params.outlet_id = currentOutlet.id;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.q.trim()) params.q = filters.q.trim();
    const { data } = await api.get('/expenses', { params });
    setExpenses(data);
  };
  useEffect(() => { load(); }, [currentOutlet, filters]);

  const shown = useMemo(() => filters.category === 'all' ? expenses : expenses.filter((e) => e.category === filters.category), [expenses, filters.category]);
  const totals = useMemo(() => ({
    incurred: shown.reduce((s, e) => s + (e.amount || 0), 0),
    paid: shown.reduce((s, e) => s + (e.paid || 0), 0),
    outstanding: shown.reduce((s, e) => s + (e.outstanding || 0), 0),
  }), [shown]);

  const uploadReceipt = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api.post('/upload', fd);
      setForm((f) => ({ ...f, receipt_url: data.url }));
      toast.success('Receipt attached');
    } catch (e) { toast.error('Receipt upload failed'); }
  };

  const save = async () => {
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Enter a valid total'); return; }
    const total = Number(form.amount);
    let paid_now = 0;
    if (form.pay_state === 'paid') paid_now = total;
    if (form.pay_state === 'partial') {
      paid_now = Number(form.paid_now || 0);
      if (paid_now <= 0 || paid_now >= total) { toast.error('Partial payment must be more than 0 and less than the total'); return; }
    }
    setBusy(true);
    try {
      const body = {
        outlet_id: currentOutlet?.id, date: form.date, category: form.category,
        description: form.description, payee: form.payee, amount: total,
        due_date: form.due_date || null, note: form.note, receipt_url: form.receipt_url || null,
        payment_mode: form.payment_mode,
      };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, body);
        toast.success('Expense updated');
      } else {
        await api.post('/expenses', { ...body, paid_now });
        toast.success('Expense recorded');
      }
      setOpen(false); setEditing(null); setForm(emptyForm);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const del = async (e) => {
    if (!window.confirm(`Delete this expense (${fmtAED(e.amount)}${e.paid ? `, ${fmtAED(e.paid)} already paid` : ''})?`)) return;
    await api.delete(`/expenses/${e.id}`);
    load(); toast.success('Expense deleted');
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ date: e.date, category: e.category, description: e.description || '', payee: e.payee || '',
      amount: e.amount, due_date: e.due_date || '', note: e.note || '', receipt_url: e.receipt_url || '',
      pay_state: 'paid', paid_now: '', payment_mode: 'cash' });
    setOpen(true);
  };

  const addPayment = async () => {
    const amt = Number(pay.amount || 0);
    if (amt <= 0) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/expenses/${payFor.id}/payments`, { ...pay, amount: amt });
      setPayFor(data); setPay(emptyPay); load();
      toast.success(data.status === 'paid' ? 'Fully paid' : `Payment recorded — ${fmtAED(data.outstanding)} outstanding`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Payment failed'); }
    finally { setBusy(false); }
  };
  const removePayment = async (pid) => {
    if (!window.confirm('Remove this payment? (audited correction)')) return;
    try {
      const { data } = await api.delete(`/expenses/${payFor.id}/payments/${pid}`);
      setPayFor(data); load(); toast.success('Payment removed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const overdue = (e) => e.due_date && e.status !== 'paid' && e.due_date < today();

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="font-display text-2xl font-bold">Expenses & Payables</h1><p className="text-sm text-muted-foreground">Track business expenses, partial payments and outstanding balances</p></div>
        <Button data-testid="add-expense-button" onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Add Expense</Button>
      </div>
      <PageHelp id="expenses">Record each expense once with its <b>total</b>, then log <b>payments</b> against it (full or partial). Status is calculated: Unpaid → Partially paid → Paid. Day Close counts the money on the day it was actually <b>paid</b>, so nothing is double-counted.</PageHelp>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <Card className="p-4"><div className="text-sm text-muted-foreground">Incurred (total)</div><div className="text-xl font-display font-bold" data-testid="expense-total-incurred">{fmtAED(totals.incurred)}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Paid</div><div className="text-xl font-display font-bold text-success" data-testid="expense-total-paid">{fmtAED(totals.paid)}</div></Card>
        <Card className="p-4"><div className="text-sm text-muted-foreground">Outstanding</div><div className="text-xl font-display font-bold text-destructive" data-testid="expense-total-outstanding">{fmtAED(totals.outstanding)}</div></Card>
      </div>

      <Card className="p-3 flex flex-wrap items-end gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid="expense-search" className="pl-8 h-9 w-52" placeholder="Payee, description, category…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
        <div><Label className="text-xs">From</Label><Input type="date" data-testid="expense-filter-start" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="h-9 w-40" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" data-testid="expense-filter-end" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="h-9 w-40" /></div>
        <div><Label className="text-xs">Category</Label>
          <select className="h-9 rounded-md border bg-background px-2" data-testid="expense-filter-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
            <option value="all">All</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <select className="h-9 rounded-md border bg-background px-2" data-testid="expense-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="all">All</option><option value="unpaid">Unpaid</option><option value="partial">Partially paid</option><option value="paid">Paid</option>
          </select>
        </div>
        {(filters.start || filters.end || filters.q || filters.category !== 'all' || filters.status !== 'all') && (
          <Button variant="ghost" size="sm" data-testid="expense-filter-clear" onClick={() => setFilters({ start: '', end: '', category: 'all', status: 'all', q: '' })}><X className="h-4 w-4 mr-1" />Clear</Button>
        )}
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Payee / Description</TableHead>
            <TableHead>Due</TableHead><TableHead className="text-right">Total</TableHead><TableHead className="text-right">Paid</TableHead>
            <TableHead className="text-right">Outstanding</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((e) => (
              <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-sm">{fmtDate(e.date)}</TableCell>
                <TableCell className="font-medium">{e.category}</TableCell>
                <TableCell className="text-sm">
                  <div>{e.payee || '—'}</div>
                  {e.description && <div className="text-xs text-muted-foreground">{e.description}</div>}
                  {e.receipt_url && <a href={`${process.env.REACT_APP_BACKEND_URL}${e.receipt_url}`} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-0.5"><Paperclip className="h-3 w-3" />Receipt</a>}
                </TableCell>
                <TableCell className={`text-sm ${overdue(e) ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>{e.due_date ? fmtDate(e.due_date) : '—'}{overdue(e) && ' · overdue'}</TableCell>
                <TableCell className="text-right font-mono">{fmtAED(e.amount)}</TableCell>
                <TableCell className="text-right font-mono text-success">{fmtAED(e.paid)}</TableCell>
                <TableCell className="text-right font-mono text-destructive">{fmtAED(e.outstanding)}</TableCell>
                <TableCell>{statusBadge(e.status)}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Payments" data-testid={`expense-payments-${e.id}`} onClick={() => { setPayFor(e); setPay({ ...emptyPay, amount: e.outstanding || '' }); }}><Banknote className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" data-testid={`expense-edit-${e.id}`} onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title="Delete" data-testid={`expense-delete-${e.id}`} onClick={() => del(e)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {shown.length === 0 && <div className="p-8 text-center text-muted-foreground" data-testid="expense-empty"><Wallet className="h-8 w-8 mx-auto mb-2 opacity-40" />No expenses match these filters</div>}
      </Card>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Expense' : 'New Expense'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Date</Label><Input type="date" data-testid="expense-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><Label>Due date (optional)</Label><Input type="date" data-testid="expense-due-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <div><Label>Category</Label><select className="w-full h-9 rounded-md border bg-background px-2" data-testid="expense-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
            <div><Label>Description</Label><Input data-testid="expense-description" placeholder="e.g. AC maintenance in kitchen" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Payee</Label><Input data-testid="expense-payee" placeholder="e.g. CoolTech LLC" value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} /></div>
              <div><Label>Total (AED)</Label><Input type="number" data-testid="expense-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
            </div>
            <div><Label>Notes</Label><Input data-testid="expense-note" placeholder="Optional" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            <div>
              <Label>Receipt (optional)</Label>
              <div className="flex items-center gap-2">
                <Input type="file" accept="image/*,.pdf" data-testid="expense-receipt" onChange={(e) => uploadReceipt(e.target.files?.[0])} className="h-9 text-xs" />
                {form.receipt_url && <Badge variant="secondary">Attached</Badge>}
              </div>
            </div>
            {!editing && (
              <div className="border rounded-lg p-3 space-y-2">
                <Label>Payment made now?</Label>
                <div className="flex gap-1.5" data-testid="expense-pay-state">
                  {[['paid', 'Fully paid'], ['partial', 'Partial'], ['unpaid', 'Unpaid']].map(([v, l]) => (
                    <button key={v} data-testid={`expense-pay-state-${v}`} onClick={() => setForm({ ...form, pay_state: v })}
                      className={`px-3 py-1.5 rounded-full text-sm border ${form.pay_state === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>{l}</button>
                  ))}
                </div>
                {form.pay_state !== 'unpaid' && (
                  <div className="grid grid-cols-2 gap-2">
                    {form.pay_state === 'partial' && (
                      <div><Label>Paid now (AED)</Label><Input type="number" data-testid="expense-paid-now" value={form.paid_now} onChange={(e) => setForm({ ...form, paid_now: e.target.value })} /></div>
                    )}
                    <div><Label>Method</Label><select className="w-full h-9 rounded-md border bg-background px-2" data-testid="expense-mode" value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })}>{MODES.map((m) => <option key={m} className="capitalize">{m}</option>)}</select></div>
                  </div>
                )}
              </div>
            )}
            {editing && <p className="text-xs text-muted-foreground">Already paid: <b>{fmtAED(editing.paid)}</b> — total can't go below this. Manage payments from the row's payment button.</p>}
            <Button className="w-full" data-testid="expense-save" disabled={busy} onClick={save}>{busy ? 'Saving…' : editing ? 'Save Changes' : 'Save Expense'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payments */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-md" data-testid="expense-payments-dialog">
          <DialogHeader><DialogTitle>Payments — {payFor?.payee || payFor?.category}</DialogTitle></DialogHeader>
          {payFor && (
            <div className="space-y-3">
              <div className="flex justify-between text-sm border rounded-lg p-2.5">
                <span>Total <b className="font-mono">{fmtAED(payFor.amount)}</b></span>
                <span className="text-success">Paid <b className="font-mono">{fmtAED(payFor.paid)}</b></span>
                <span className="text-destructive">Outstanding <b className="font-mono" data-testid="payments-outstanding">{fmtAED(payFor.outstanding)}</b></span>
              </div>
              <div className="space-y-1.5 max-h-44 overflow-y-auto" data-testid="payment-history">
                {(payFor.payments || []).length === 0 && <div className="text-sm text-muted-foreground">No payments yet</div>}
                {(payFor.payments || []).map((p) => (
                  <div key={p.id} className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm" data-testid={`payment-row-${p.id}`}>
                    <div>
                      <div className="font-mono font-medium">{fmtAED(p.amount)} <Badge variant="secondary" className="capitalize ml-1">{p.method}</Badge></div>
                      <div className="text-xs text-muted-foreground">{fmtDate(p.date)}{p.reference ? ` · Ref ${p.reference}` : ''}{p.notes ? ` · ${p.notes}` : ''} · by {p.by || '—'}</div>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" title="Remove (audited)" data-testid={`payment-remove-${p.id}`} onClick={() => removePayment(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                ))}
              </div>
              {payFor.outstanding > 0 ? (
                <div className="border-t pt-3 space-y-2">
                  <Label>Record a payment</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-xs">Amount (AED)</Label><Input type="number" data-testid="payment-amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
                    <div><Label className="text-xs">Date</Label><Input type="date" data-testid="payment-date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></div>
                    <div><Label className="text-xs">Method</Label><select className="w-full h-9 rounded-md border bg-background px-2" data-testid="payment-method" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>{MODES.map((m) => <option key={m} className="capitalize">{m}</option>)}</select></div>
                    <div><Label className="text-xs">Reference</Label><Input data-testid="payment-reference" placeholder="Cheque / txn no." value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></div>
                  </div>
                  <Input data-testid="payment-notes" placeholder="Notes (optional)" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} />
                  <Button className="w-full" data-testid="payment-add" disabled={busy} onClick={addPayment}>{busy ? 'Saving…' : 'Add Payment'}</Button>
                </div>
              ) : (
                <div className="text-sm text-success font-medium text-center pt-1" data-testid="payments-settled">Fully settled</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
